import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import StateObserver from 'src/StateObserver';
import { Avatar } from '../components/shared/Avatar';
import bitmapFonts from 'src/lib/bitmapFonts';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { createEmitter } from 'src/lib/Emitter';
import platform from '@play-co/gcinstant';
import { Level } from 'src/replicant/buruburu/level';

const ui = uiConfig.buruburu.profile;

export default class PopupProfile extends PopupBasic {
  private content: FlexBoxView;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 660,
      darkerBg: false,
      tracking: { feature: 'profile', $subFeature: 'popup-profile' },
      titleIcon: { image: 'assets/ui/buruburu/profile_icon.png' },
    });

    this.buttonClose.removeFromSuperview();

    // Top Separator
    new View({
      superview: this.box,
      height: 5,
      width: this.box.style.width / 2,
      x: this.box.style.width / 2,
      y: 90,
      backgroundColor: '#e7b77e',
      centerOnOrigin: true,
    });

    this.content = new FlexBoxView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 120,
      width: this.box.style.width * 0.9,
      height: this.box.style.height - 245,
      flex: {
        flexDirection: 'column',
        justifyContent: 'space-evenly',
      },
    });

    // Bottom Separator
    new View({
      superview: this.box,
      height: 5,
      width: this.box.style.width / 2,
      x: this.box.style.width / 2,
      y: this.box.style.height - 140,
      backgroundColor: '#e7b77e',
      centerOnOrigin: true,
    });

    this.message.updateOpts({
      visible: false,
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...ui.button,
      superview: this.box,
      x: this.box.style.width / 2,
      y: this.box.style.height - 75,
      localeText: () => i18n('basic.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: { feature: 'profile', $subFeature: 'popup-profile-close' },
    });
  }

  public init() {
    super.init({
      title: i18n('profile.title'),
    });

    const {
      user: {
        level: { current: level },
      },
      pet,
    } = StateObserver.getState();

    // Profile section.

    const profileView = new View({
      superview: this.content,
      width: this.content.style.width,
      height: 100,
    });

    const avatar = new Avatar({
      superview: profileView,
      width: 100,
      height: 100,
      x: -profileView.style.width * 0.5,
      y: -profileView.style.height * 0.25,
      centerOnOrigin: false,
    });

    const frame = new ImageScaleView({
      ...ui.profile.contentPanel,
      superview: profileView,
      width: profileView.style.width * 0.8,
      x: this.content.style.width * (0.5 - 0.8),
      centerOnOrigin: false,
    });

    const name = new LangBitmapFontTextView({
      ...ui.profile.titleText,
      superview: frame,
      x:
        ui.profile.frameMargin +
        frame.style.width * 0.5 -
        (frame.style.width * 0.4) / 2,
      y: frame.style.height * 0.5,
      width: frame.style.width * 0.6,
      height: frame.style.height,
    });

    createEmitter(
      this.box,
      ({ user: { publicProfile } }) => publicProfile,
    ).addListener((publicProfile) => {
      name.localeText = () => publicProfile?.name ?? '...';
      avatar.updateImage(publicProfile?.photo);
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.primary,
      superview: frame,
      localeText: () => i18n('profile.editAction'),
      fontSize: 25,
      font: bitmapFonts('Body'),
      x: frame.style.width - 60,
      y: frame.style.height * 0.5,
      width: 120,
      height: 62,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('ok');
        await openPopupPromise('popupEditProfile', {});
      },
      tracking: {
        feature: 'profile',
        $subFeature: 'pupup-profile-editProfile',
      },
    });

    // Stats section.

    this.createSection(
      i18n('profile.level'),
      i18n('attributes.level', {
        level: Level.ruleset.toDisplay(level),
      }).replace(/\n/, ''),
    );
    this.createSection(i18n('profile.buruburuRaised'), '0');
    this.createSection(i18n('profile.marriages'), '0');
    this.createSection(
      i18n('profile.highestAge'),
      i18n('attributes.age', {
        age: pet[platform.playerID]?.attributes.age ?? 0,
      }),
    );
  }

  private createSection(title: string, data: string) {
    const frame = new ImageScaleView({
      ...ui.profile.contentPanel,
      superview: this.content,
      width: this.content.style.width,
    });

    new LangBitmapFontTextView({
      ...ui.profile.titleText,
      superview: frame,
      x: ui.profile.frameMargin + frame.style.width * 0.5,
      y: frame.style.height * 0.5,
      width: frame.style.width,
      height: frame.style.height,
      localeText: () => title,
    });

    new LangBitmapFontTextView({
      ...ui.profile.dataText,
      superview: frame,
      x:
        frame.style.width - (ui.profile.frameMargin + frame.style.width * 0.25),
      y: frame.style.height * 0.5,
      width: frame.style.width * 0.5,
      height: frame.style.height,
      localeText: () => data,
    });
  }
}
